import { Controller } from "@hotwired/stimulus"

/**
 * Issue Reference Controller
 *
 * Handles the reference search autocomplete in the issue show sidebar.
 * When the user selects a reference type and types a search query,
 * this controller fetches matching records and lets the user pick one.
 *
 * Targets:
 *   searchWrap    – container for search input + results (hidden until type selected)
 *   searchInput   – text input for search query
 *   results       – container for search result items
 *   selectedId    – hidden input holding the chosen record's ID
 *   selectedDisplay – visible element showing the chosen record's name
 */
export default class extends Controller {
  static targets = ["searchWrap", "searchInput", "results", "selectedId", "selectedDisplay"]

  connect() {
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  // Called when the type <select> changes
  typeChanged(event) {
    const type = event.target.value
    if (type) {
      this.searchWrapTarget.classList.remove("hidden")
      this.searchInputTarget.value = ""
      this.searchInputTarget.focus()
      this.resultsTarget.innerHTML = ""
      this.clearSelection()
    } else {
      this.searchWrapTarget.classList.add("hidden")
      this.resultsTarget.innerHTML = ""
      this.clearSelection()
    }
  }

  // Called on input in the search field (debounced)
  search() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    const query = this.searchInputTarget.value.trim()
    if (query.length < 2) {
      this.resultsTarget.innerHTML = ""
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 250)
  }

  async fetchResults(query) {
    const typeSelect = this.element.querySelector("select[name='referenceable_type']")
    const type = typeSelect ? typeSelect.value : ""
    if (!type) return

    try {
      const url = `/issues/search_references?type=${encodeURIComponent(type)}&q=${encodeURIComponent(query)}`
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        this.resultsTarget.innerHTML = `<p class="text-xs text-red-500 p-1">Search failed</p>`
        return
      }

      const data = await response.json()
      this.renderResults(data)
    } catch (err) {
      this.resultsTarget.innerHTML = `<p class="text-xs text-red-500 p-1">Network error</p>`
    }
  }

  renderResults(results) {
    if (results.length === 0) {
      this.resultsTarget.innerHTML = `<p class="text-xs text-mod-text-muted p-1">No matches found</p>`
      return
    }

    this.resultsTarget.innerHTML = results.map(item => `
      <button type="button"
        class="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-mod-warm-100 dark:hover:bg-white/10 text-mod-charcoal dark:text-white transition-colors cursor-pointer"
        data-id="${this.escapeHtml(String(item.id))}"
        data-name="${this.escapeHtml(item.name)}"
        data-action="click->issue-reference#selectResult">
        ${this.escapeHtml(item.name)}
      </button>
    `).join("")
  }

  selectResult(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const id = btn.dataset.id
    const name = btn.dataset.name

    this.selectedIdTarget.value = id
    this.selectedDisplayTarget.textContent = `✓ ${name}`
    this.selectedDisplayTarget.classList.remove("hidden")
    this.resultsTarget.innerHTML = ""
    this.searchInputTarget.value = ""
  }

  clearSelection() {
    this.selectedIdTarget.value = ""
    this.selectedDisplayTarget.textContent = ""
    this.selectedDisplayTarget.classList.add("hidden")
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.appendChild(document.createTextNode(str))
    return div.innerHTML
  }
}
